import { useEffect, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { AlertTriangle, ShieldAlert } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Pill } from '@/components/widgets/Pill';
import { adminDashboard, adminEmergency } from '@/api/admin';
import { fmtMoney, fmtPct } from '@/lib/format';
import { ApiError } from '@/lib/http';
import { toast } from '@/components/ui/sonner';
import { useForceLiquidateStore } from './state';

/** 同时挂载 Step1 + Step2 两个弹窗 — 由 store.phase 决定显示哪个 */
export function ForceLiquidateDialogs() {
  const phase = useForceLiquidateStore((s) => s.phase);
  return (
    <>
      <Step1 open={phase === 'step1'} />
      <Step2 open={phase === 'step2'} />
    </>
  );
}

// ─── Step 1: 总览 + 不可逆警告 ──────────────────────────────────

function Step1({ open }: { open: boolean }) {
  const close = useForceLiquidateStore((s) => s.close);
  const toStep2 = useForceLiquidateStore((s) => s.toStep2);
  // 弹出时拉一次仪表板,展示总览;关闭时不重新拉
  const dashboardQ = useQuery({
    queryKey: ['dashboard', 'force-liquidate-overview'],
    queryFn: adminDashboard.get,
    enabled: open,
    staleTime: 0,
  });

  return (
    <Dialog open={open} onOpenChange={(v) => !v && close()}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-down">
            <AlertTriangle className="h-5 w-5" />
            一键强制平仓 · 第 1 步,共 2 步
          </DialogTitle>
          <DialogDescription>
            该操作会立即关闭主账户与全部 20 个模拟账户的所有持仓,撤销挂单,并锁定交易至次日开仓时段。
            <span className="ml-1 font-medium text-down">不可撤销</span>。
          </DialogDescription>
        </DialogHeader>

        <div className="rounded-lg border border-line bg-bg-3 p-4 space-y-2 text-sm">
          {dashboardQ.isLoading && <div className="text-ink-2">加载实时账户总览...</div>}
          {dashboardQ.isError && <div className="text-down">无法获取账户总览,请稍后重试</div>}
          {dashboardQ.data && (
            <>
              <div className="flex justify-between">
                <span className="text-ink-2">主账户净值</span>
                <span className="num font-medium">{fmtMoney(dashboardQ.data.mainAccount.equity)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-ink-2">浮动盈亏</span>
                <span className="num font-medium">{fmtMoney(dashboardQ.data.mainAccount.floatingPnl, { sign: true })}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-ink-2">保证金占用</span>
                <span className="num">{fmtPct(dashboardQ.data.mainAccount.marginUsagePct)}</span>
              </div>
              <div className="flex justify-between border-t border-line pt-2">
                <span className="text-ink-2">总持仓笔数(主 + 雇员)</span>
                <Pill tone="warn">{dashboardQ.data.employeeSummary.totalOpenPositions} 笔</Pill>
              </div>
              <div className="flex justify-between">
                <span className="text-ink-2">在线雇员</span>
                <span className="num">
                  {dashboardQ.data.employeeSummary.online} / {dashboardQ.data.employeeSummary.total}
                </span>
              </div>
            </>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={close}>
            取消
          </Button>
          <Button variant="destructive" onClick={toStep2} disabled={dashboardQ.isLoading}>
            继续(下一步填写原因)
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Step 2: reason ─────────────────────────────────────────────

function Step2({ open }: { open: boolean }) {
  const close = useForceLiquidateStore((s) => s.close);
  const setSubmitting = useForceLiquidateStore((s) => s.setSubmitting);
  const [reason, setReason] = useState('');

  useEffect(() => {
    if (open) {
      setReason('');
    }
  }, [open]);

  const submit = useMutation({
    mutationFn: () => adminEmergency.forceLiquidation({ reason: reason.trim() }),
    onMutate: () => setSubmitting(true),
    onSettled: () => setSubmitting(false),
    onSuccess: () => {
      toast.success('已触发强平', { description: '后端开始执行,进度将通过 WebSocket 实时推送' });
      close();
    },
    onError: (err) => {
      const msg = err instanceof ApiError ? err.message : '提交失败';
      toast.error('强平触发失败', { description: msg });
    },
  });

  const reasonOk = reason.trim().length >= 4;
  const canSubmit = reasonOk && !submit.isPending;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && close()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-down">
            <ShieldAlert className="h-5 w-5" />
            一键强制平仓 · 第 2 步,共 2 步
          </DialogTitle>
          <DialogDescription>请填写本次强平的操作原因。</DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="fl-reason">操作原因(至少 4 字)</Label>
            <Input
              id="fl-reason"
              placeholder="例如:API 错误率持续上升,主账户净值接近水位线"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              disabled={submit.isPending}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={close} disabled={submit.isPending}>
            取消
          </Button>
          <Button variant="destructive" onClick={() => submit.mutate()} disabled={!canSubmit}>
            {submit.isPending ? '提交中...' : '确认强平'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
